import { ContactList } from "@/components/contact-list";
import { DisplayKv } from "@/components/display-kv";
import { DomainView } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useSheetContext } from "@/context/sheet";
import { useDeleteHotel, useGetHotel } from "@/hooks/hotels";
import { useGetRegion } from "@/hooks/regions";
import { T } from "@/i18n";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Key } from "react";

export const Route = createFileRoute("/(hotels)/hotels/_sheet/$id/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data, isLoading } = useGetHotel(id);
  const { data: region, isLoading: regionLoading } = useGetRegion(
    data?.regionId?.toString() ?? "",
    {
      enabled: typeof data?.regionId === "number",
    }
  );
  const { close } = useSheetContext();
  const navigate = useNavigate();
  const { mutate: deleteHotel } = useDeleteHotel({
    onSuccess: () => {
      close(() => {
        navigate({ to: "/hotels" });
      });
    },
  });

  return (
    <DomainView
      domain="hotels"
      editLink={(children, key) => (
        <Link to="/hotels/$id/edit" params={{ id }} key={key as Key}>
          {children}
        </Link>
      )}
      isLoading={isLoading}
      title={data?.name}
      onDelete={() => deleteHotel(id)}
      propsList={[
        {
          title: "hotels.fields.priority",
          value: data?.priority || "-",
        },
        {
          title: "hotels.fields.socialReason",
          value: data?.socialReason || "-",
        },
        {
          title: "hotels.fields.group",
          value: data?.group || "-",
        },
        {
          title: "hotels.fields.category",
          value: data?.category || "-",
        },
        {
          title: "hotels.fields.iban",
          value: data?.iban || "-",
        },
        {
          title: "hotels.fields.vat",
          value: data?.vat || "-",
        },
        {
          title: "hotels.fields.paymentAfterInvoice",
          value: <T tKey={`common.boolean.${!!data?.paymentAfterInvoice}`} />,
        },
        {
          title: "hotels.fields.bookingDiscount",
          value: data?.bookingDiscount || "-",
        },
        {
          title: "hotels.fields.hasBar",
          value: <T tKey={`common.boolean.${!!data?.hasBar}`} />,
        },
        {
          title: "hotels.fields.hasBar",
          value: (
            <DisplayKv
              list={Object.entries(data?.bar ?? {}).map(([key, value]) => ({
                label: key,
                value,
              }))}
            />
          ),
        },
        {
          title: "hotels.fields.phoneNumber",
          value: formatPhoneNumber(data?.phoneNumber),
        },
        {
          title: "hotels.fields.address",
          value: `${data?.address}${data?.zipCode ? `, ${data.zipCode}` : ""}${data?.locality ? `, ${data.locality}` : ""}`,
        },
        {
          title: "hotels.fields.regionId",
          value:
            (regionLoading || !region) && typeof data?.regionId === "number" ? (
              <Skeleton className="h-6 w-full" />
            ) : (
              region?.name || "-"
            ),
        },
        {
          title: "hotels.fields.contacts",
          value: data?.contacts?.length ? (
            <ContactList list={data?.contacts ?? []} />
          ) : (
            "-"
          ),
        },
        {
          title: "hotels.fields.checkInTime",
          value: data?.checkInTime || "-",
        },
        {
          title: "hotels.fields.checkOutTime",
          value: data?.checkOutTime || "-",
        },
        {
          title: "hotels.info.breakfast",
          value: `${data?.breakfastStartTime ?? ""} - ${data?.breakfastEndTime ?? ""}`,
        },
        {
          title: "hotels.fields.kidFriendly",
          value: <T tKey={`common.boolean.${!!data?.kidFriendly}`} />,
        },
        {
          title: "hotels.fields.childMaxAge",
          value: data?.childMaxAge ?? "-",
        },
        {
          title: "hotels.fields.hasPicnic",
          value: <T tKey={`common.boolean.${!!data?.hasPicnic}`} />,
        },
        {
          enabled: !!data?.hasPicnic,
          title: "hotels.fields.picnicPrice",
          value: data?.picnicPrice ?? "-",
        },
        {
          enabled: !!data?.hasPicnic,
          title: "hotels.fields.picnicNotes",
          value: data?.picnicNotes ?? "-",
        },
        {
          title: "hotels.fields.hasDinner",
          value: <T tKey={`common.boolean.${!!data?.hasDinner}`} />,
        },
        {
          enabled: !!data?.hasDinner,
          title: "hotels.fields.dinnerPrice",
          value: data?.dinnerPrice ?? "-",
        },
        {
          enabled: !!data?.hasDinner,
          title: "hotels.fields.dinnerAutoScheduling",
          value: <T tKey={`common.boolean.${!!data?.dinnerAutoScheduling}`} />,
        },
        {
          enabled: !!data?.hasDinner,
          title: "hotels.fields.dinnerNotes",
          value: data?.dinnerNotes,
        },
        {
          title: "hotels.fields.bikeStorage",
          value: <T tKey={`common.boolean.${!!data?.bikeStorage}`} />,
        },
        {
          enabled: !!data?.bikeStorage,
          title: "hotels.fields.bikeStoragePrice",
          value: data?.bikeStoragePrice ?? "-",
        },
        {
          enabled: !!data?.bikeStorage,
          title: "hotels.fields.bikeStorageQuantity",
          value: data?.bikeStorageQuantity ?? "-",
        },
        {
          enabled: !!data?.bikeStorage,
          title: "hotels.fields.bikeNotes",
          value: data?.bikeNotes || "-",
        },
        {
          title: "hotels.fields.petFriendly",
          value: <T tKey={`common.boolean.${!!data?.petFriendly}`} />,
        },
        {
          enabled: !!data?.petFriendly,
          title: "hotels.fields.petNotes",
          value: data?.petNotes || "-",
        },
        {
          title: "hotels.fields.internalObservations",
          value: data?.internalObservations || "-",
        },
        {
          title: "hotels.fields.externalObservations",
          value: data?.externalObservations || "-",
        },
      ]}
    />
  );
}
